/**
 * AvatarUpload Component
 * 
 * Handles uploading and managing the BOT AVATAR (chatbot.avatar_url)
 * This avatar is shown in:
 * - Builder header
 * - Chatbot listing pages
 * 
 * NOTE: This is NOT the operator avatar that appears in chat messages.
 * Chat messages use the operator_avatar_url field.
 */

import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useChatbotBaseMutations } from '@/hooks/mutations/useChatbotBaseMutations';
import { useParams } from 'react-router-dom';

interface AvatarUploadProps {
  avatarUrl?: string | null; // Bot avatar URL (chatbot.avatar_url)
  name: string;
}

export const AvatarUpload = ({ avatarUrl, name }: AvatarUploadProps) => {
  const { id } = useParams();
  const [isHovering, setIsHovering] = useState(false);
  const { uploadAvatar } = useChatbotBaseMutations(id);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
      alert('Please upload an image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    
    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return;
    }
    
    uploadAvatar.mutate({ file });
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <label htmlFor="avatar-upload" className="cursor-pointer block">
        <Avatar className="h-24 w-24 cursor-pointer">
          <AvatarImage src={avatarUrl || undefined} alt={name} />
          <AvatarFallback className="text-2xl">{getInitials(name)}</AvatarFallback>
        </Avatar>
      </label>
      
      <input
        id="avatar-upload"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
      
      {isHovering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full pointer-events-none">
          <div className="text-white text-xs font-medium">
            {avatarUrl ? 'Change Avatar' : 'Upload Avatar'}
          </div>
          
          {avatarUrl && (
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white absolute bottom-0 right-0 pointer-events-auto"
              onClick={(e) => {
                e.stopPropagation();
                uploadAvatar.mutate({ file: new File([], 'empty.txt') });
              }}
            >
              <X size={16} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
